import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import Modal from './Modal';
import './AlarmManagementModal.css';

const Editor = lazy(() => import('@monaco-editor/react'));

const AlarmManagementModal = ({ isOpen, onClose }) => {
  const [alarms, setAlarms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAlarmForm, setShowAlarmForm] = useState(false);
  const [editingAlarm, setEditingAlarm] = useState(null);
  
  const [formData, setFormData] = useState({
    alarm_name: '',
    description: '',
    is_common: false,
    enabled: true,
    alarm_dashboard_id: '',
    check_interval: 2,
    snooze_duration: 300,
    condition_sql: '',
    on_confirm_function: '',
    on_confirm_parameters: {},
    priority: 0,
    severity: 'info',
    popup_size_mode: 'fixed',
    popup_width: null,
    popup_height: null,
    popup_padding_vertical: 40,
    popup_padding_horizontal: 40,
    popup_transparent_mode: true
  });
  
  const [dbFunctions, setDbFunctions] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [sqlTestResult, setSqlTestResult] = useState(null);
  const [sqlTesting, setSqlTesting] = useState(false);
  const [functionFilter, setFunctionFilter] = useState('');
  const [dashboardFilter, setDashboardFilter] = useState('');
  const [popupVariables, setPopupVariables] = useState([]); // 선택된 대시보드의 publish 변수들
  
  // 필터링된 DB 함수 목록
  const filteredDbFunctions = useMemo(() => {
    if (!functionFilter.trim()) return dbFunctions;
    const filter = functionFilter.toLowerCase();
    return dbFunctions.filter(func => 
      func.name.toLowerCase().includes(filter) ||
      (func.description && func.description.toLowerCase().includes(filter))
    );
  }, [dbFunctions, functionFilter]);
  
  // 필터링된 대시보드 목록
  const filteredDashboards = useMemo(() => {
    if (!dashboardFilter.trim()) return dashboards;
    const filter = dashboardFilter.toLowerCase();
    return dashboards.filter(dashboard => 
      dashboard.title.toLowerCase().includes(filter) ||
      dashboard.dashboard_id.toLowerCase().includes(filter)
    );
  }, [dashboards, dashboardFilter]);
  
  // 필터 초기화 함수
  const resetFilters = () => {
    setFunctionFilter('');
    setDashboardFilter('');
  };
  
  // 모달 닫기 핸들러 (필터 초기화)
  const handleClose = () => {
    resetFilters();
    onClose();
  };
  
  const loadAlarms = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/visualization/alarms/', {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const result = await response.json();
      
      if (result.success && Array.isArray(result.alarms)) {
        setAlarms(result.alarms);
      } else {
        setError(result.error || '알람 목록을 불러오지 못했습니다');
        setAlarms([]);
      }
    } catch (err) {
      console.error('[AlarmManagement] 알람 목록 로드 오류:', err);
      setError('알람 목록 로드 중 오류가 발생했습니다');
      setAlarms([]);
    } finally {
      setLoading(false);
    }
  };
  
  const loadUserInfo = async () => {
    try {
      const response = await fetch('/api/users/me/', {
        method: 'GET',
        credentials: 'include'
      });
      const data = await response.json();
      setIsAdmin(data.is_staff || data.is_superuser);
    } catch (err) {
      console.error('[AlarmManagement] 사용자 정보 로드 오류:', err);
      setIsAdmin(false);
    }
  };
  
  const loadDbFunctions = async () => {
    try {
      const response = await fetch('/api/dashboard/function/allowed/', {
        method: 'GET',
        credentials: 'include'
      });
      const result = await response.json();
      
      if (result.success && Array.isArray(result.functions)) {
        setDbFunctions(result.functions);
      }
    } catch (err) {
      console.error('[AlarmManagement] DB 함수 목록 로드 오류:', err);
    }
  };
  
  const loadDashboards = async () => {
    try {
      const response = await fetch('/api/visualization/user-dashboards/', {
        method: 'GET',
        credentials: 'include'
      });
      const result = await response.json();
      
      if (Array.isArray(result.dashboards)) {
        setDashboards(result.dashboards);
      }
    } catch (err) {
      console.error('[AlarmManagement] 대시보드 목록 로드 오류:', err);
    }
  };
  
  // 선택된 대시보드의 publish 변수 추출
  const loadDashboardPublishVariables = async (dashboardId) => {
    if (!dashboardId) {
      setPopupVariables([]);
      return;
    }
    
    try {
      // 대시보드 템플릿 조회
      const response = await fetch(`/api/visualization/user-dashboards/`, {
        method: 'GET',
        credentials: 'include'
      });
      const result = await response.json();
      
      if (!Array.isArray(result.dashboards)) {
        setPopupVariables([]);
        return;
      }
      
      // 선택된 대시보드 찾기
      const selectedDashboard = result.dashboards.find(
        d => d.dashboard_id === dashboardId
      );
      
      if (!selectedDashboard || !selectedDashboard.template) {
        setPopupVariables([]);
        return;
      }
      
      // 템플릿에서 publish_variable 추출
      const template = selectedDashboard.template;
      const publishVars = new Set();
      
      if (template.components && Array.isArray(template.components)) {
        template.components.forEach(component => {
          // 1. on_click.publish 확인 (가장 일반적인 패턴)
          if (component.on_click && component.on_click.publish && typeof component.on_click.publish === 'object') {
            Object.keys(component.on_click.publish).forEach(varName => {
              publishVars.add(varName);
            });
          }
          
          // 2. publish_variable 필드 확인 (레거시)
          if (component.publish_variable) {
            publishVars.add(component.publish_variable);
          }
          
          // 3. chart_options.publish_variable도 확인 (일부 컴포넌트)
          if (component.chart_options && component.chart_options.publish_variable) {
            publishVars.add(component.chart_options.publish_variable);
          }
          
          // 4. interactions.onClick.publishVariables도 확인 (다른 형식)
          if (component.interactions && 
              component.interactions.onClick && 
              component.interactions.onClick.publishVariables) {
            Object.keys(component.interactions.onClick.publishVariables).forEach(varName => {
              publishVars.add(varName);
            });
          }
        });
      }
      
      const varList = Array.from(publishVars).sort();
      console.log(`[AlarmManagement] 대시보드 '${dashboardId}'의 publish 변수:`, varList);
      setPopupVariables(varList);
      
    } catch (err) {
      console.error('[AlarmManagement] 대시보드 publish 변수 로드 오류:', err);
      setPopupVariables([]);
    }
  };
  
  useEffect(() => {
    if (isOpen) {
      loadAlarms();
      loadUserInfo();
      loadDbFunctions();
      loadDashboards();
    }
  }, [isOpen]);
  
  // 알람 대시보드가 선택되면 해당 대시보드의 publish 변수 로드
  useEffect(() => {
    if (formData.alarm_dashboard_id) {
      loadDashboardPublishVariables(formData.alarm_dashboard_id);
    } else {
      setPopupVariables([]);
    }
  }, [formData.alarm_dashboard_id]);
  
  const handleAddAlarm = () => {
    setEditingAlarm(null);
    setFormData({
      alarm_name: '',
      description: '',
      is_common: false,
      enabled: true,
      alarm_dashboard_id: '',
      check_interval: 2,
      snooze_duration: 300,
      condition_sql: '',
      on_confirm_function: '',
      on_confirm_parameters: {},
      priority: 0,
      severity: 'info',
      popup_size_mode: 'fixed',
      popup_width: null,
      popup_height: null,
      popup_padding_vertical: 40,
      popup_padding_horizontal: 40,
      popup_transparent_mode: true
    });
    setSqlTestResult(null);
    setShowAlarmForm(true);
  };
  
  const handleEditAlarm = (alarm) => {
    setEditingAlarm(alarm);
    setFormData({
      alarm_name: alarm.alarm_name,
      description: alarm.description || '',
      is_common: alarm.is_common,
      enabled: alarm.enabled,
      alarm_dashboard_id: alarm.alarm_dashboard_id,
      check_interval: alarm.check_interval,
      snooze_duration: alarm.snooze_duration,
      condition_sql: alarm.condition_sql,
      on_confirm_function: alarm.on_confirm_function || '',
      on_confirm_parameters: alarm.on_confirm_parameters || {},
      priority: alarm.priority,
      severity: alarm.severity,
      popup_size_mode: alarm.popup_size_mode || 'fixed',
      popup_width: alarm.popup_width || null,
      popup_height: alarm.popup_height || null,
      popup_padding_vertical: alarm.popup_padding_vertical || 40,
      popup_padding_horizontal: alarm.popup_padding_horizontal || 40,
      popup_transparent_mode: alarm.popup_transparent_mode === true  // 실제 값 사용 (신규 알람만 기본 true)
    });
    setSqlTestResult(null);
    setShowAlarmForm(true);
  };
  
  const handleDeleteAlarm = async (alarm) => {
    if (!window.confirm(`알람 "${alarm.alarm_name}"을(를) 삭제하시겠습니까?`)) {
      return;
    }
    
    try {
      const response = await fetch(`/api/visualization/alarms/${alarm.id}/`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(result.message || '알람이 삭제되었습니다');
        loadAlarms();
      } else {
        alert(result.error || '알람 삭제에 실패했습니다');
      }
    } catch (err) {
      console.error('[AlarmManagement] 알람 삭제 오류:', err);
      alert('알람 삭제 중 오류가 발생했습니다');
    }
  };
  
  const handleSaveAlarm = async () => {
    if (!formData.alarm_name.trim()) {
      alert('알람 이름을 입력하세요');
      return;
    }
    if (!formData.alarm_dashboard_id) {
      alert('알람 대시보드를 선택하세요');
      return;
    }
    if (!formData.condition_sql.trim()) {
      alert('알람 조건 SQL을 입력하세요');
      return;
    }
    
    // SQL 형식 검증
    const validation = validateSql(formData.condition_sql);
    if (!validation.valid) {
      alert(`SQL 형식 오류: ${validation.error}`);
      return;
    }
    
    try {
      const url = editingAlarm 
        ? `/api/visualization/alarms/${editingAlarm.id}/`
        : '/api/visualization/alarms/create/';
      
      const method = editingAlarm ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setShowAlarmForm(false);
        setEditingAlarm(null);
        resetFilters();
        loadAlarms();
      } else {
        alert(result.error || '알람 저장에 실패했습니다');
      }
    } catch (err) {
      console.error('[AlarmManagement] 알람 저장 오류:', err);
      alert('알람 저장 중 오류가 발생했습니다');
    }
  };
  
  // SQL 형식 검증 함수
  const validateSql = (sql) => {
    const trimmedSql = sql.trim().toUpperCase();
    
    // 1. SELECT 문으로 시작하는지 확인
    if (!trimmedSql.startsWith('SELECT')) {
      return { valid: false, error: 'SELECT 문만 허용됩니다' };
    }
    
    // 2. 위험한 키워드 차단 (SQL Injection 방지)
    const dangerousKeywords = [
      'INSERT', 'UPDATE', 'DELETE', 'DROP', 'TRUNCATE', 
      'ALTER', 'CREATE', 'EXEC', 'EXECUTE', '--', '/*'
    ];
    
    for (const keyword of dangerousKeywords) {
      if (trimmedSql.includes(keyword)) {
        return { valid: false, error: `금지된 키워드: ${keyword}` };
      }
    }
    
    // 3. 세미콜론 여러 개 차단 (다중 쿼리 방지)
    const semicolonCount = (sql.match(/;/g) || []).length;
    if (semicolonCount > 1) {
      return { valid: false, error: '다중 쿼리는 허용되지 않습니다' };
    }
    
    return { valid: true };
  };
  
  const handleTestSql = async () => {
    if (!formData.condition_sql.trim()) {
      alert('SQL 쿼리를 입력하세요');
      return;
    }
    
    // SQL 형식 검증
    const validation = validateSql(formData.condition_sql);
    if (!validation.valid) {
      setSqlTestResult({
        success: false,
        message: `SQL 형식 오류: ${validation.error}`
      });
      return;
    }
    
    setSqlTesting(true);
    setSqlTestResult(null);
    
    try {
      const response = await fetch('/api/visualization/UpdateChartData/', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sql: formData.condition_sql,
          chart_template: {
            chart_options: {
              viz_type: 'table'
            }
          }
        })
      });
      
      const result = await response.json();
      
      console.log('[AlarmManagement] SQL 테스트 응답:', result);
      
      // UpdateChartData API 응답 구조: { chart_data: { chart_options: { data: { rows, columns } } } }
      const rows = result.chart_data?.chart_options?.data?.rows;
      const columns = result.chart_data?.chart_options?.data?.columns;
      
      if (rows && Array.isArray(rows) && rows.length > 0) {
        // rows는 2D 배열: [[true, "1"]]
        // columns로 필드명 확인: ["should_show", "alarm_key"]
        const firstRow = rows[0];
        
        // 필드명 검증: should_show, alarm_key 필수
        if (!columns || !Array.isArray(columns)) {
          setSqlTestResult({
            success: false,
            message: '쿼리 결과에 컬럼 정보가 없습니다'
          });
          return;
        }
        
        const hasRequiredFields = columns.includes('should_show') && columns.includes('alarm_key');
        if (!hasRequiredFields) {
          setSqlTestResult({
            success: false,
            message: `필수 필드가 누락되었습니다. 필요: should_show, alarm_key / 실제: ${columns.join(', ')}`
          });
          return;
        }
        
        // 정확히 2개 필드만 허용
        if (columns.length !== 2) {
          setSqlTestResult({
            success: false,
            message: `필드 개수가 잘못되었습니다. 필요: 2개 (should_show, alarm_key) / 실제: ${columns.length}개 (${columns.join(', ')})`
          });
          return;
        }
        
        // 행 데이터를 객체로 변환
        const rowObject = {};
        columns.forEach((col, idx) => {
          rowObject[col] = firstRow[idx];
        });
        
        setSqlTestResult({
          success: true,
          data: rowObject,
          message: `✅ SQL 형식 검증 통과! (${rows.length}행 반환)`
        });
      } else if (rows && Array.isArray(rows) && rows.length === 0) {
        setSqlTestResult({
          success: false,
          message: '쿼리 결과가 없습니다 (0행 반환)'
        });
      } else if (result.error) {
        setSqlTestResult({
          success: false,
          message: result.error
        });
      } else {
        console.error('[AlarmManagement] 예상치 못한 응답 구조:', result);
        setSqlTestResult({
          success: false,
          message: 'SQL 실행 실패: 알 수 없는 응답 형식'
        });
      }
    } catch (err) {
      setSqlTestResult({
        success: false,
        message: '오류: ' + err.message
      });
    } finally {
      setSqlTesting(false);
    }
  };
  
  const handleToggleAlarm = async (alarm) => {
    try {
      const response = await fetch(`/api/visualization/alarms/${alarm.id}/`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...alarm,
          enabled: !alarm.enabled
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        loadAlarms();
      } else {
        alert(result.error || '알람 상태 변경에 실패했습니다');
      }
    } catch (err) {
      console.error('[AlarmManagement] 알람 토글 오류:', err);
      alert('알람 상태 변경 중 오류가 발생했습니다');
    }
  };
  
  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="alarm-management-backdrop" onClick={handleClose}>
        <div className="alarm-management-modal" onClick={(e) => e.stopPropagation()}>
          <div className="alarm-management-header">
            <h3>🔔 알람 관리</h3>
            <button className="close-button" onClick={handleClose}>✕</button>
          </div>
          
          <div className="alarm-management-body">
            {loading ? (
              <div className="loading-state">알람 목록을 불러오는 중...</div>
            ) : error ? (
              <div className="error-state">
                <span className="error-icon">⚠️</span>
                <span>{error}</span>
              </div>
            ) : !showAlarmForm ? (
              <>
                <div className="alarm-list-header">
                  <button className="add-alarm-btn" onClick={handleAddAlarm}>
                    + 새 알람 추가
                  </button>
                </div>
                
                {alarms.length === 0 ? (
                  <div className="empty-state">
                    <span className="empty-icon">📭</span>
                    <p>등록된 알람이 없습니다</p>
                    <p className="empty-hint">위의 버튼을 클릭하여 새 알람을 추가하세요</p>
                  </div>
                ) : (
                  <div className="alarm-list">
                    {alarms.map(alarm => (
                      <div key={alarm.id} className={`alarm-card ${!alarm.enabled ? 'disabled' : ''}`}>
                        <div className="alarm-card-header">
                          <div className="alarm-title-section">
                            <label className="alarm-toggle">
                              <input
                                type="checkbox"
                                checked={alarm.enabled}
                                onChange={() => handleToggleAlarm(alarm)}
                              />
                              <span className="toggle-slider"></span>
                            </label>
                            <div className="alarm-title-info">
                              <h4>{alarm.alarm_name}</h4>
                              <div className="alarm-badges">
                                {alarm.is_common && (
                                  <span className="badge badge-common">테넌트 공통</span>
                                )}
                                <span className={`badge badge-${alarm.severity}`}>
                                  {alarm.severity === 'critical' ? '긴급' : 
                                   alarm.severity === 'warning' ? '주의' : '정보'}
                                </span>
                                {alarm.priority > 0 && (
                                  <span className="badge badge-priority">우선순위: {alarm.priority}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="alarm-actions">
                            <button 
                              className="edit-btn"
                              onClick={() => handleEditAlarm(alarm)}
                              title="수정"
                            >
                              ✏️
                            </button>
                            <button 
                              className="delete-btn"
                              onClick={() => handleDeleteAlarm(alarm)}
                              title="삭제"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                        
                        <div className="alarm-card-body">
                          {alarm.description && (
                            <p className="alarm-description">{alarm.description}</p>
                          )}
                          <div className="alarm-info-grid">
                            <div className="info-item">
                              <span className="info-label">대시보드:</span>
                              <span className="info-value">{alarm.alarm_dashboard_id}</span>
                            </div>
                            <div className="info-item">
                              <span className="info-label">체크 주기:</span>
                              <span className="info-value">{alarm.check_interval}초</span>
                            </div>
                            <div className="info-item">
                              <span className="info-label">스누즈:</span>
                              <span className="info-value">{Math.floor(alarm.snooze_duration / 60)}분</span>
                            </div>
                            {alarm.on_confirm_function && (
                              <div className="info-item">
                                <span className="info-label">확인 함수:</span>
                                <span className="info-value">{alarm.on_confirm_function}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="alarm-form">
                <div className="form-header">
                  <h4>{editingAlarm ? '알람 수정' : '새 알람 추가'}</h4>
                  <button 
                    className="back-btn"
                    onClick={() => {
                      setShowAlarmForm(false);
                      setEditingAlarm(null);
                      resetFilters();
                    }}
                  >
                    ← 목록으로
                  </button>
                </div>
                
                <div className="form-body">
                  <div className="form-group">
                    <label>알람 이름 *</label>
                    <input
                      type="text"
                      value={formData.alarm_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, alarm_name: e.target.value }))}
                      placeholder="예: 배터리 과열 알람"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>알람 설명</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="알람에 대한 설명을 입력하세요"
                      rows={2}
                    />
                  </div>
                  
                  {isAdmin && (
                    <div className="form-group">
                      <label>알람 유형</label>
                      <div className="radio-group">
                        <label>
                          <input
                            type="radio"
                            checked={!formData.is_common}
                            onChange={() => setFormData(prev => ({ ...prev, is_common: false }))}
                          />
                          <span>개인 알람 (나만 보기)</span>
                        </label>
                        <label>
                          <input
                            type="radio"
                            checked={formData.is_common}
                            onChange={() => setFormData(prev => ({ ...prev, is_common: true }))}
                          />
                          <span>테넌트 공통 알람 (모든 사용자)</span>
                        </label>
                      </div>
                    </div>
                  )}
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>심각도 *</label>
                      <select
                        value={formData.severity}
                        onChange={(e) => setFormData(prev => ({ ...prev, severity: e.target.value }))}
                      >
                        <option value="critical">긴급 (Critical)</option>
                        <option value="warning">주의 (Warning)</option>
                        <option value="info">정보 (Info)</option>
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label>우선순위</label>
                      <input
                        type="number"
                        value={formData.priority ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData(prev => ({ 
                            ...prev, 
                            priority: val === '' ? '' : parseInt(val, 10)
                          }));
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '' || isNaN(parseInt(e.target.value, 10))) {
                            setFormData(prev => ({ ...prev, priority: 0 }));
                          }
                        }}
                        min="0"
                        placeholder="0"
                      />
                      <small>높을수록 먼저 표시됩니다</small>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>알람 대시보드 *</label>
                    <input
                      type="text"
                      placeholder="대시보드 검색..."
                      value={dashboardFilter}
                      onChange={(e) => setDashboardFilter(e.target.value)}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    <select
                      value={formData.alarm_dashboard_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, alarm_dashboard_id: e.target.value }))}
                    >
                      <option value="">선택...</option>
                      {filteredDashboards.map(d => (
                        <option key={d.id} value={d.dashboard_id || d.id}>
                          {d.title || d.dashboard_id}
                        </option>
                      ))}
                    </select>
                    <small>알람 발생 시 팝업으로 표시할 대시보드 ({filteredDashboards.length}/{dashboards.length}개)</small>
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>체크 주기 (초)</label>
                      <input
                        type="number"
                        value={formData.check_interval ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData(prev => ({ 
                            ...prev, 
                            check_interval: val === '' ? '' : parseInt(val, 10)
                          }));
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '' || isNaN(parseInt(e.target.value, 10))) {
                            setFormData(prev => ({ ...prev, check_interval: 2 }));
                          }
                        }}
                        min="1"
                        placeholder="2"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>스누즈 시간 (초)</label>
                      <input
                        type="number"
                        value={formData.snooze_duration ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormData(prev => ({ 
                            ...prev, 
                            snooze_duration: val === '' ? '' : parseInt(val, 10)
                          }));
                        }}
                        onBlur={(e) => {
                          if (e.target.value === '' || isNaN(parseInt(e.target.value, 10))) {
                            setFormData(prev => ({ ...prev, snooze_duration: 300 }));
                          }
                        }}
                        min="60"
                        step="60"
                        placeholder="300"
                      />
                    </div>
                  </div>
                  
                  {/* 팝업 크기 설정 섹션 */}
                  <div className="form-group">
                    <label>팝업 크기 모드</label>
                    <select
                      value={formData.popup_size_mode}
                      onChange={(e) => setFormData(prev => ({ ...prev, popup_size_mode: e.target.value }))}
                    >
                      <option value="fixed">고정 (기본 크기)</option>
                      <option value="manual">수동 (직접 지정)</option>
                      <option value="auto">자동 (콘텐츠 기반)</option>
                    </select>
                    <small>
                      {formData.popup_size_mode === 'fixed' && '기본 고정 크기로 팝업을 표시합니다 (85% 너비, 최대 1000px)'}
                      {formData.popup_size_mode === 'manual' && '팝업 크기를 직접 지정합니다'}
                      {formData.popup_size_mode === 'auto' && '대시보드 콘텐츠 크기에 맞춰 자동으로 조정합니다'}
                    </small>
                  </div>
                  
                  {formData.popup_size_mode === 'manual' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>팝업 너비 (px)</label>
                        <input
                          type="number"
                          value={formData.popup_width || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            popup_width: e.target.value ? parseInt(e.target.value, 10) : null 
                          }))}
                          min="500"
                          max="2000"
                          placeholder="800"
                        />
                        <small>최소 500px (버튼 영역 포함), 최대 2000px</small>
                      </div>
                      
                      <div className="form-group">
                        <label>팝업 높이 (px)</label>
                        <input
                          type="number"
                          value={formData.popup_height || ''}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            popup_height: e.target.value ? parseInt(e.target.value, 10) : null 
                          }))}
                          min="400"
                          max="1500"
                          placeholder="600"
                        />
                        <small>최소 400px (버튼 영역 포함), 최대 1500px</small>
                      </div>
                    </div>
                  )}
                  
                  {formData.popup_size_mode === 'auto' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>상하 여백 (px)</label>
                        <input
                          type="number"
                          value={formData.popup_padding_vertical ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ 
                              ...prev, 
                              popup_padding_vertical: val === '' ? '' : parseInt(val, 10)
                            }));
                          }}
                          onBlur={(e) => {
                            if (e.target.value === '' || isNaN(parseInt(e.target.value, 10))) {
                              setFormData(prev => ({ ...prev, popup_padding_vertical: 40 }));
                            }
                          }}
                          min="0"
                          max="200"
                          placeholder="40"
                        />
                        <small>콘텐츠 기반 크기에 추가할 상하 여백</small>
                      </div>
                      
                      <div className="form-group">
                        <label>좌우 여백 (px)</label>
                        <input
                          type="number"
                          value={formData.popup_padding_horizontal ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setFormData(prev => ({ 
                              ...prev, 
                              popup_padding_horizontal: val === '' ? '' : parseInt(val, 10)
                            }));
                          }}
                          onBlur={(e) => {
                            if (e.target.value === '' || isNaN(parseInt(e.target.value, 10))) {
                              setFormData(prev => ({ ...prev, popup_padding_horizontal: 40 }));
                            }
                          }}
                          min="0"
                          max="200"
                          placeholder="40"
                        />
                        <small>콘텐츠 기반 크기에 추가할 좌우 여백</small>
                      </div>
                    </div>
                  )}
                  
                  {/* 투명 팝업 모드 */}
                  <div className="form-group" style={{ marginTop: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.popup_transparent_mode}
                        onChange={(e) => setFormData(prev => ({ ...prev, popup_transparent_mode: e.target.checked }))}
                      />
                      <span>투명 팝업 모드</span>
                    </label>
                    <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
                      활성화 시 팝업 프레임(배경, 테두리, 그림자)이 숨겨지고 대시보드만 표시됩니다.
                    </small>
                  </div>
                  
                  <div className="form-group">
                    <label>알람 선택 SQL *</label>
                    <div className="sql-editor-container">
                      <Suspense fallback={<div>에디터 로딩 중...</div>}>
                        <Editor
                          height="250px"
                          language="sql"
                          value={formData.condition_sql}
                          onChange={(value) => setFormData(prev => ({ ...prev, condition_sql: value || '' }))}
                          options={{
                            minimap: { enabled: false },
                            lineNumbers: 'on',
                            scrollBeyondLastLine: false,
                            fontSize: 14,
                            tabSize: 2,
                            wordWrap: 'on',
                            automaticLayout: true
                          }}
                        />
                      </Suspense>
                    </div>
                    <small>
                      ⚠️ SELECT 문만 허용 | 필수 필드: should_show (boolean), alarm_key (text) | 정확히 2개 필드만 반환
                    </small>
                    
                    <button 
                      className="test-sql-btn"
                      onClick={handleTestSql}
                      disabled={sqlTesting}
                    >
                      {sqlTesting ? '테스트 중...' : 'SQL 테스트 실행'}
                    </button>
                    
                    {sqlTestResult && (
                      <div className={`sql-test-result ${sqlTestResult.success ? 'success' : 'error'}`}>
                        <div><strong>{sqlTestResult.message}</strong></div>
                        {sqlTestResult.data && (
                          <pre>{JSON.stringify(sqlTestResult.data, null, 2)}</pre>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>확인 버튼 DB 함수</label>
                    <input
                      type="text"
                      placeholder="함수 검색..."
                      value={functionFilter}
                      onChange={(e) => setFunctionFilter(e.target.value)}
                      style={{ marginBottom: '0.5rem' }}
                    />
                    <select
                      value={formData.on_confirm_function}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        setFormData(prev => ({ ...prev, on_confirm_function: selectedValue }));

                        // 선택된 값에서 스키마.함수명 형태로 파싱하여 함수 찾기
                        const selectedFunc = dbFunctions.find(f => {
                          const fullName = `${f.schema}.${f.name}`;
                          return fullName === selectedValue || f.name === selectedValue;
                        });

                        if (selectedFunc && selectedFunc.parameters) {
                          // 파라미터 초기화 (빈 값으로)
                          const newParams = {};
                          selectedFunc.parameters.forEach(param => {
                            newParams[param.name] = '';
                          });
                          setFormData(prev => ({ ...prev, on_confirm_parameters: newParams }));
                        }
                      }}
                    >
                      <option value="">선택...</option>
                      {filteredDbFunctions.map((func, index) => {
                        const fullName = `${func.schema}.${func.name}`;
                        return (
                          <option key={`${func.schema}_${func.name}_${index}`} value={fullName}>
                            {fullName} - {func.comment || func.description || '설명 없음'}
                          </option>
                        );
                      })}
                    </select>
                    <small>알람 확인 시 실행할 DB 함수 (선택 사항) ({filteredDbFunctions.length}/{dbFunctions.length}개)</small>
                  </div>
                  
                  {formData.on_confirm_function && dbFunctions.find(f => {
                    const fullName = `${f.schema}.${f.name}`;
                    return fullName === formData.on_confirm_function || f.name === formData.on_confirm_function;
                  })?.parameters && (
                    <div className="form-group">
                      <label>함수 파라미터</label>
                      <div className="parameter-list" style={{ display: 'block' }}>
                        {dbFunctions.find(f => {
                          const fullName = `${f.schema}.${f.name}`;
                          return fullName === formData.on_confirm_function || f.name === formData.on_confirm_function;
                        })?.parameters.map(param => {
                          const currentValue = formData.on_confirm_parameters[param.name] || '';
                          
                          return (
                            <div 
                              key={param.name} 
                              style={{ 
                                marginBottom: '1.5rem',
                                display: 'block',
                                width: '100%'
                              }}
                            >
                              {/* 파라미터 이름 (1줄) */}
                              <div style={{ 
                                marginBottom: '0.5rem',
                                display: 'block',
                                width: '100%'
                              }}>
                                <label style={{ 
                                  fontWeight: '500', 
                                  fontSize: '0.95rem',
                                  display: 'block'
                                }}>
                                  {param.name} ({param.type})
                                </label>
                              </div>
                              
                              {/* 변수 선택 드롭다운 (2줄) */}
                              <div style={{ 
                                marginBottom: '0.5rem',
                                display: 'block',
                                width: '100%'
                              }}>
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      setFormData(prev => ({
                                        ...prev,
                                        on_confirm_parameters: {
                                          ...prev.on_confirm_parameters,
                                          [param.name]: e.target.value
                                        }
                                      }));
                                    }
                                  }}
                                  style={{ 
                                    width: '100%',
                                    padding: '0.6rem', 
                                    fontSize: '0.9rem',
                                    borderRadius: '6px',
                                    border: '1px solid #ddd',
                                    backgroundColor: '#f8f9fa',
                                    display: 'block'
                                  }}
                                >
                                  <option value="">변수를 선택하거나 아래에 직접 입력하세요</option>
                                  <optgroup label="시스템 변수">
                                    <option value="${alarm_key}">${'{alarm_key}'} - 알람 키</option>
                                    <option value="${current_user}">${'{current_user}'} - 현재 사용자</option>
                                    <option value="${current_timestamp}">${'{current_timestamp}'} - 현재 시간</option>
                                  </optgroup>
                                  {popupVariables.length > 0 && (
                                    <optgroup label={`팝업 변수 (${formData.alarm_dashboard_id || '선택된 대시보드'})`}>
                                      {popupVariables.map(varName => (
                                        <option key={varName} value={`\${popup.${varName}}`}>
                                          ${`{popup.${varName}}`} - 팝업 내 '{varName}' 변수
                                        </option>
                                      ))}
                                    </optgroup>
                                  )}
                                  {popupVariables.length === 0 && formData.alarm_dashboard_id && (
                                    <optgroup label="팝업 변수">
                                      <option disabled>선택된 대시보드에 publish 변수가 없습니다</option>
                                    </optgroup>
                                  )}
                                </select>
                                <small style={{ 
                                  display: 'block', 
                                  marginTop: '0.3rem', 
                                  color: '#666',
                                  fontSize: '0.85rem'
                                }}>
                                  💡 팁: 팝업 대시보드에서 publishVariables()로 변수를 설정하면 ${'{'} popup.변수명 {'}'}으로 접근 가능
                                  {popupVariables.length > 0 && (
                                    <span style={{ display: 'block', color: '#28a745', marginTop: '0.2rem' }}>
                                      ✓ 현재 {popupVariables.length}개 변수 감지됨: {popupVariables.join(', ')}
                                    </span>
                                  )}
                                </small>
                              </div>
                              
                              {/* 직접 입력창 (3줄) */}
                              <div style={{ 
                                display: 'block',
                                width: '100%'
                              }}>
                                <input
                                  type="text"
                                  placeholder="값 입력"
                                  value={currentValue}
                                  onChange={(e) => {
                                    setFormData(prev => ({
                                      ...prev,
                                      on_confirm_parameters: {
                                        ...prev.on_confirm_parameters,
                                        [param.name]: e.target.value
                                      }
                                    }));
                                  }}
                                  style={{ 
                                    width: '100%',
                                    padding: '0.6rem',
                                    fontSize: '0.9rem',
                                    borderRadius: '6px',
                                    border: '1px solid #ddd',
                                    display: 'block',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <small>
                        💡 변수를 선택하면 입력창에 자동으로 채워집니다
                      </small>
                    </div>
                  )}
                </div>
                
                <div className="form-footer">
                  <button 
                    className="cancel-btn"
                    onClick={() => {
                      setShowAlarmForm(false);
                      setEditingAlarm(null);
                      resetFilters();
                    }}
                  >
                    취소
                  </button>
                  <button 
                    className="save-btn"
                    onClick={handleSaveAlarm}
                  >
                    {editingAlarm ? '수정' : '등록'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AlarmManagementModal;

